import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, User, Eye, Search, Filter, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { useNotifications } from '../hooks/useNotifications';
import { useSiteTranslation } from '../hooks/useSiteTranslation';
import {
  getPostTitle,
  getPostExcerpt,
  getBlogCategoryLabelKey,
  BLOG_CATEGORY_VALUES
} from '../i18n/localizeContent';
import { Button } from '../components/UI';

interface Post {
  _id: string;
  title: string;
  content: string;
  excerpt: string;
  i18n?: Record<string, { title?: string; excerpt?: string; content?: string }>;
  slug: string;
  author: {
    name: string;
  };
  category: string;
  tags: string[];
  featuredImage: string;
  isPublished: boolean;
  isFeatured: boolean;
  views: number;
  likes: number;
  publishedAt: string;
  createdAt: string;
}

const Blog: React.FC = () => {
  const { ts, t, siteName, i18n } = useSiteTranslation();
  const lang = i18n.language;
  const [posts, setPosts] = useState<Post[]>([]);
  const [featuredPosts, setFeaturedPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const notifications = useNotifications();
  
  // Newsletter state
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [isNewsletterSubmitting, setIsNewsletterSubmitting] = useState(false);
  const [newsletterStatus, setNewsletterStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [newsletterMessage, setNewsletterMessage] = useState('');

  const categories = BLOG_CATEGORY_VALUES;


  // Usar useRef para prevenir múltiplas notificações
  const errorNotifiedRef = useRef(false);

  const fetchPosts = useCallback(async () => {
    try {
      setLoading(true);
      // Resetar flag quando começar nova requisição
      errorNotifiedRef.current = false;
      
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '6',
        ...(searchTerm && { search: searchTerm }),
        ...(selectedCategory && { category: selectedCategory }),
      });

      const response = await axios.get(`/posts?${params}`);
      setPosts(response.data.posts);
      setTotalPages(response.data.totalPages);
      // Sucesso: resetar flag
      errorNotifiedRef.current = false;
    } catch (error: any) {
      console.error('Erro ao carregar posts:', error);
      // Apenas mostrar notificação uma vez por sessão de erros
      if (!errorNotifiedRef.current && !axios.isCancel(error) && error.code !== 'ECONNABORTED') {
        notifications.error.network();
        errorNotifiedRef.current = true;
      }
      // Fallback para posts estáticos em caso de erro
      setPosts([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, selectedCategory, currentPage]);

  const fetchFeaturedPosts = useCallback(async () => {
    try {
      const response = await axios.get('/posts?featured=true&limit=3');
      setFeaturedPosts(response.data.posts);
    } catch (error: any) {
      console.error('Erro ao carregar posts em destaque:', error);
      setFeaturedPosts([]);
    }
  }, []);

  useEffect(() => {
    fetchPosts();
    fetchFeaturedPosts();
  }, [fetchPosts, fetchFeaturedPosts]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newsletterEmail.trim()) return;

    try {
      setIsNewsletterSubmitting(true);
      setNewsletterStatus('idle');

      await axios.post('/newsletter/subscribe', {
        email: newsletterEmail.trim(),
        source: 'blog'
      });
      
      setNewsletterStatus('success');
      setNewsletterMessage('Inscrição realizada com sucesso!');
      notifications.success.newsletterSubscribed();
      setNewsletterEmail('');
    } catch (error: any) {
      setNewsletterStatus('error');
      const errorMessage = error.response?.data?.message || t('footer.subscribeError');
      setNewsletterMessage(errorMessage);
      notifications.error.newsletterSubscribe(errorMessage);
    } finally {
      setIsNewsletterSubmitting(false);
    }
  };

  const getCategoryColor = (category: string) => {
    const colors: { [key: string]: string } = {
      'imigração': 'bg-blue-100 text-blue-800',
      'documentos': 'bg-green-100 text-green-800',
      'vistos': 'bg-purple-100 text-purple-800',
      'cidadania': 'bg-yellow-100 text-yellow-800',
      'trabalho': 'bg-red-100 text-red-800',
      'estudos': 'bg-indigo-100 text-indigo-800',
      'geral': 'bg-gray-100 text-gray-800',
    };
    return colors[category] || colors['geral'];
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="hero-gradient section-padding">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              {ts('blog.title')}
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">{ts('blog.subtitle')}</p>
            
            {/* Search Bar */}
            <div className="max-w-2xl mx-auto">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder={t('blog.searchPh')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="field pl-12 pr-4 text-lg"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="bg-white py-8 border-b border-gray-200">
        <div className="container-custom">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center space-x-4">
              <Filter className="w-5 h-5 text-gray-500" />
              <span className="text-gray-700 font-medium">{t('blog.filterCategory')}</span>
            </div>
            
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedCategory('')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
                  selectedCategory === '' 
                    ? 'bg-primary-600 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {t('blog.all')}
              </button>
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
                    selectedCategory === category 
                      ? 'bg-primary-600 text-white' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {t(getBlogCategoryLabelKey(category))}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Featured Posts */}
      {featuredPosts.length > 0 && (
        <section className="section-padding bg-gray-50">
          <div className="container-custom">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">{t('blog.featured')}</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {featuredPosts.map((post) => (
                <article key={post._id} className="card group hover:-translate-y-2 transition-transform duration-300">
                  <Link to={`/post/${post.slug || post._id}`}>
                    {post.featuredImage && (
                      <div className="aspect-w-16 aspect-h-9 mb-6 overflow-hidden rounded-lg">
                        <img 
                          src={post.featuredImage} 
                          alt={getPostTitle(post, lang)}
                          className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    )}
                    
                    <div className="flex items-center space-x-2 mb-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getCategoryColor(post.category)}`}>
                        {t(getBlogCategoryLabelKey(post.category))}
                      </span>
                      <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium">
                        {t('blog.featuredBadge')}
                      </span>
                    </div>
                    
                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-primary-600 transition-colors duration-200">
                      {getPostTitle(post, lang)}
                    </h3>
                  </Link>
                  
                  <p className="text-gray-600 mb-4 line-clamp-3">
                    {getPostExcerpt(post, lang)}
                  </p>
                  
                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-1">
                        <User className="w-4 h-4" />
                        <span>{post.author?.name || t('blog.authorUndefined')}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Calendar className="w-4 h-4" />
                        <span>{formatDate(post.publishedAt)}</span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Eye className="w-4 h-4" />
                      <span>{post.views}</span>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Recent Posts */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">{t('blog.recent')}</h2>
          
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, index) => (
                <div key={index} className="card animate-pulse">
                  <div className="h-48 bg-gray-200 rounded-lg mb-6"></div>
                  <div className="h-4 bg-gray-200 rounded mb-4"></div>
                  <div className="h-4 bg-gray-200 rounded mb-4 w-3/4"></div>
                  <div className="h-4 bg-gray-200 rounded mb-4 w-1/2"></div>
                </div>
              ))}
            </div>
          ) : posts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post) => (
                <article key={post._id} className="card group hover:-translate-y-2 transition-transform duration-300">
                  <Link to={`/post/${post.slug || post._id}`}>
                    {post.featuredImage && (
                      <div className="aspect-w-16 aspect-h-9 mb-6 overflow-hidden rounded-lg">
                        <img 
                          src={post.featuredImage} 
                          alt={getPostTitle(post, lang)}
                          className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    )}
                    
                    <div className="flex items-center space-x-2 mb-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getCategoryColor(post.category)}`}>
                        {t(getBlogCategoryLabelKey(post.category))}
                      </span>
                      {post.isFeatured && (
                        <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium">
                          {t('blog.featuredBadge')}
                        </span>
                      )}
                    </div>
                    
                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-primary-600 transition-colors duration-200">
                      {getPostTitle(post, lang)}
                    </h3>
                  </Link>
                  
                  <p className="text-gray-600 mb-4 line-clamp-3">
                    {getPostExcerpt(post, lang)}
                  </p>
                  
                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-1">
                        <User className="w-4 h-4" />
                        <span>{post.author?.name || t('blog.authorUndefined')}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Calendar className="w-4 h-4" />
                        <span>{formatDate(post.publishedAt)}</span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Eye className="w-4 h-4" />
                      <span>{post.views}</span>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="text-gray-400 mb-4">
                <Search className="w-16 h-16 mx-auto" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('blog.emptyTitle')}</h3>
              <p className="text-gray-600 mb-6">{t('blog.emptySubtitle')}</p>
              <Button
                onClick={() => {
                  setSearchTerm('');
                  setSelectedCategory('');
                }}>
                {t('blog.clearFilters')}
              </Button>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center mt-12">
              <div className="flex items-center space-x-2">
                <Button variant="ghost" size="sm"
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}>
                  {t('blog.previous')}
                </Button>
                
                {[...Array(totalPages)].map((_, index) => (
                  <button
                    key={index + 1}
                    onClick={() => setCurrentPage(index + 1)}
                    className={`px-4 py-2 rounded-lg ${
                      currentPage === index + 1
                        ? 'bg-primary-600 text-white'
                        : 'border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {index + 1}
                  </button>
                ))}
                
                <Button variant="ghost" size="sm"
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}>
                  {t('blog.next')}
                </Button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Newsletter */}
      <section className="section-padding bg-gradient-to-r from-primary-600 to-secondary-600 text-white">
        <div className="container-custom text-center">
          <h2 className="text-4xl font-bold mb-6">{t('blog.newsletterTitle')}</h2>
          <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">{t('blog.newsletterDesc')}</p>
          <form onSubmit={handleNewsletterSubmit} className="max-w-md mx-auto flex flex-col sm:flex-row gap-4">
            <input
              type="email"
              value={newsletterEmail}
              onChange={(e) => setNewsletterEmail(e.target.value)}
              placeholder={t('footer.emailPlaceholder')}
              className="flex-1 px-4 py-3 rounded-lg text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-white focus:outline-none"
              required
              disabled={isNewsletterSubmitting}
            />
            <button 
              type="submit"
              disabled={isNewsletterSubmitting || !newsletterEmail.trim()}
              className="bg-white text-primary-600 hover:bg-gray-100 font-semibold py-3 px-6 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isNewsletterSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
                  {t('footer.subscribing')}
                </>
              ) : (
                t('footer.subscribe')
              )}
            </button>
          </form>
          
          {/* Status Messages */}
          {newsletterStatus === 'success' && (
            <div className="mt-4 flex items-center justify-center gap-2 text-green-300">
              <CheckCircle className="w-4 h-4" />
              <span className="text-sm">{newsletterMessage}</span>
            </div>
          )}
          
          {newsletterStatus === 'error' && (
            <div className="mt-4 flex items-center justify-center gap-2 text-red-300">
              <AlertCircle className="w-4 h-4" />
              <span className="text-sm">{newsletterMessage}</span>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Blog;
